import { Helmet } from "react-helmet-async";
import { gsap } from "../animations/gsapSetup.ts";
import { TennisCourtSvg } from "../components/sports/TennisCourtSvg.tsx";
import { useGsapScrollTrigger } from "../hooks/useGsapScrollTrigger.ts";
import { useReducedMotion } from "../hooks/useReducedMotion.ts";
import { useI18n } from "../i18n/context.tsx";
import { SITE_URL } from "../utils/constants.ts";
import styles from "./SportClassic.module.css";

const VOICE_BARS = [14, 28, 20, 36, 24, 32, 18, 30, 26, 16, 34, 22];

export function TennisClassicPage() {
  const { t } = useI18n();
  const reducedMotion = useReducedMotion();

  /* Hero */
  const heroRef = useGsapScrollTrigger((el) => {
    if (reducedMotion) return;
    const title = el.querySelector("[data-hero-title]");
    const sub = el.querySelector("[data-hero-sub]");
    if (title) {
      gsap.set(title, { scale: 0.85, opacity: 0 });
      gsap.to(title, { scale: 1, opacity: 1, duration: 1.2, ease: "power3.out", delay: 0.2 });
    }
    if (sub) {
      gsap.set(sub, { y: 30, opacity: 0 });
      gsap.to(sub, { y: 0, opacity: 1, duration: 0.8, ease: "power3.out", delay: 0.6 });
    }
  }, []);

  /* Voice bars */
  const voiceRef = useGsapScrollTrigger((el) => {
    if (reducedMotion) return;
    const bars = el.querySelectorAll("[data-bar]");
    gsap.set(bars, { scaleY: 0.2, opacity: 0 });
    gsap.to(bars, {
      scaleY: 1,
      opacity: 1,
      duration: 0.6,
      stagger: 0.05,
      ease: "power3.out",
      scrollTrigger: { trigger: el, start: "top 75%", toggleActions: "play none none reverse" },
    });
  }, []);

  /* Analytics SVG */
  const analyticsRef = useGsapScrollTrigger((el) => {
    if (reducedMotion) return;
    gsap.set(el, { y: 40, opacity: 0 });
    gsap.to(el, {
      y: 0,
      opacity: 1,
      duration: 0.8,
      ease: "power3.out",
      scrollTrigger: { trigger: el, start: "top 80%", toggleActions: "play none none reverse" },
    });
  }, []);

  /* Features */
  const featRef = useGsapScrollTrigger((el) => {
    if (reducedMotion) return;
    const cards = el.querySelectorAll("[data-feat]");
    gsap.set(cards, { y: 40, opacity: 0 });
    gsap.to(cards, {
      y: 0,
      opacity: 1,
      duration: 0.6,
      stagger: 0.1,
      ease: "power3.out",
      scrollTrigger: { trigger: el, start: "top 75%", toggleActions: "play none none reverse" },
    });
  }, []);

  /* CTA */
  const ctaRef = useGsapScrollTrigger((el) => {
    if (reducedMotion) return;
    gsap.set(el, { scale: 0.95, opacity: 0 });
    gsap.to(el, {
      scale: 1,
      opacity: 1,
      duration: 0.7,
      ease: "power3.out",
      scrollTrigger: { trigger: el, start: "top 80%", toggleActions: "play none none reverse" },
    });
  }, []);

  return (
    <>
      <Helmet>
        <title>ShootCreator Tennis Classic - Voice-Controlled Stroke Counter</title>
        <meta
          name="description"
          content="Tennis stroke counter with T/Body/Wide serve zone analytics. Voice-controlled counting, Apple Watch, session history. Free."
        />
        <link rel="canonical" href={`${SITE_URL}/tennis/classic`} />
      </Helmet>

      {/* Hero */}
      <section ref={heroRef} className={`${styles.hero} ${styles.heroBgTennis}`}>
        <h1 className={`${styles.heroWordmark} ${styles.accentTennis}`} data-hero-title>
          {t.tennis.heroWordmark}
        </h1>
        <p className={styles.heroSubtitle} data-hero-sub>
          {t.tennis.heroSubtitle}
        </p>
      </section>

      {/* Voice */}
      <section ref={voiceRef} className={`${styles.section} ${styles.sectionAlt}`}>
        <h2 className={styles.sectionTitle}>{t.tennis.voiceTitle}</h2>
        <p className={styles.sectionDesc}>{t.tennis.voiceDesc}</p>
        <div className={styles.voiceBars}>
          {VOICE_BARS.map((h, i) => (
            <div
              key={`b${String(i)}`}
              className={styles.voiceBar}
              data-bar
              style={{ height: h, background: "#4caf50" }}
            />
          ))}
        </div>
      </section>

      {/* Court Analytics */}
      <section ref={analyticsRef} className={styles.section}>
        <h2 className={styles.sectionTitle}>{t.tennis.analyticsTitle}</h2>
        <p className={styles.sectionDesc}>{t.tennis.analyticsDesc}</p>
        <div className={styles.svgWrap}>
          <TennisCourtSvg />
        </div>
      </section>

      {/* Features */}
      <section ref={featRef} className={`${styles.section} ${styles.sectionAlt}`}>
        <h2 className={styles.sectionTitle}>{t.tennis.classicTitle}</h2>
        <div className={styles.featGrid}>
          {t.tennis.features.map((f) => (
            <div key={f.title} className={styles.featCard} data-feat>
              <span className={styles.featIcon}>{f.icon}</span>
              <h3 className={styles.featTitle}>{f.title}</h3>
              <p className={styles.featDesc}>{f.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section ref={ctaRef} className={styles.cta}>
        <h2 className={styles.ctaTitle}>{t.tennis.ctaTitle}</h2>
        <button type="button" className={`${styles.ctaButton} ${styles.ctaButtonTennis}`}>
          {t.tennis.ctaButton}
        </button>
        <p className={styles.ctaNote}>{t.tennis.ctaNote}</p>
      </section>
    </>
  );
}
